/**
 * SkeletonDebugWidget
 *
 * Debug widget for visualizing raycast data from skeleton generation
 * and controlling the skeleton processing pipeline passes.
 */

import { DebugControlsWidget } from '../../../utils/DebugControlsWidget';
import { IViewerScene, toggleDebugGroupVisibility } from '../../../utils/DebugVisualization';
import { SkeletonProcessingPipeline } from '../../../utils/SkeletonProcessingPipeline';
import { PipelineControlPanel, PipelineInterface } from '../../../ui/pipeline/PipelineControlPanel';

export const SKELETON_RAYCAST_DEBUG_GROUP_NAME = 'skeleton-raycast-debug';
export const SKELETON_BISECTOR_DEBUG_GROUP_NAME = 'skeleton-bisector-debug';
export const SKELETON_STRAIGHTEN_DEBUG_GROUP_NAME = 'skeleton-straighten-debug';
export const SKELETON_SOURCE_VERTEX_DEBUG_GROUP_NAME = 'skeleton-source-vertex-debug';
export const SKELETON_PROBE_ORIGIN_DEBUG_GROUP_NAME = 'skeleton-probe-origin-debug';
export const SKELETON_PROBE_RAY_DEBUG_GROUP_NAME = 'skeleton-probe-ray-debug';
export const SKELETON_PROBE_MIDPOINT_DEBUG_GROUP_NAME = 'skeleton-probe-midpoint-debug';
export const SKELETON_PROBE_CENTERLINE_DEBUG_GROUP_NAME = 'skeleton-probe-centerline-debug';

// Pair-walls pass debug groups (one per escalation stage). Each stage
// only fires for sources still unowned after earlier stages, so toggling
// these independently lets the user see exactly which stage produced
// each ray without the four stages drowning each other out.
export const SKELETON_PAIR_WALLS_MATCH_NEAR_DEBUG_GROUP_NAME = 'skeleton-pair-walls-match-near-debug';
export const SKELETON_PAIR_WALLS_RAY_NEAR_DEBUG_GROUP_NAME = 'skeleton-pair-walls-ray-near-debug';
export const SKELETON_PAIR_WALLS_MATCH_INF_DEBUG_GROUP_NAME = 'skeleton-pair-walls-match-inf-debug';
export const SKELETON_PAIR_WALLS_RAY_INF_DEBUG_GROUP_NAME = 'skeleton-pair-walls-ray-inf-debug';

/** Prune-pass matching ties: polygon evidence → locked/rejected trace. */
export const SKELETON_PRUNE_MATCH_DEBUG_GROUP_NAME = 'skeleton-prune-match-debug';

/** Thickness corner-match ties: skeleton node → claimed polygon corner. */
export const SKELETON_CORNER_MATCH_DEBUG_GROUP_NAME = 'skeleton-corner-match-debug';

/** Per-element wall-partition boundary lines (edge quads + node n-gons). */
export const SKELETON_SPLIT_LINES_DEBUG_GROUP_NAME = 'skeleton-split-lines-debug';

/** Rasterized wall region from the raster-thin skeleton pass (textured plane). */
export const SKELETON_RASTER_DEBUG_GROUP_NAME = 'skeleton-raster-debug';

/** Debug options for skeleton visualization */
export interface SkeletonDebugOptions {
  /** Show raycast visualization */
  showRaycasts: boolean;
  /** Show bisector directions */
  showBisectors: boolean;
  /** Show straighten rays */
  showStraightenRays: boolean;
  /** Show lines from each node to its source vertices */
  showSourceVertices: boolean;
  /** Show probe origins (small dots 5cm in along each adjacent edge) */
  showProbeOrigins: boolean;
  /** Show probe rays (perpendicular to source edge, into wall material) */
  showProbeRays: boolean;
  /** Show centerline midpoints (one per probe, on the wall centerline) */
  showProbeMidpoints: boolean;
  /** Show centerlines (parallel to source edge through midpoint) */
  showProbeCenterlines: boolean;
  /** Pair-walls stage 1: parallel match within 50 cm. */
  showPairWallsMatchNear: boolean;
  /** Pair-walls stage 2: perpendicular ray hit within 50 cm. */
  showPairWallsRayNear: boolean;
  /** Pair-walls stage 3: parallel match with no distance cap. */
  showPairWallsMatchInf: boolean;
  /** Pair-walls stage 4: perpendicular ray hit with no distance cap. */
  showPairWallsRayInf: boolean;
  /** Show the prune pass's matching ties (green = locked, red = rejected). */
  showPruneMatches: boolean;
  /** Show the thickness pass's corner-match ties (node → claimed corner). */
  showCornerMatches: boolean;
  /** Show the per-element wall partition boundaries (edge quads + node n-gons). */
  showSplitLines: boolean;
  /** Show the rasterized wall region produced by the raster-thin skeleton pass. */
  showRasterImage: boolean;
  /** Debug: disable the crease-edge filter so every mesh triangle edge is drawn. */
  showAllTriangles: boolean;
}

/** Callbacks for skeleton debug widget */
export interface SkeletonDebugCallbacks {
  onDebugOptionsChange?: (options: SkeletonDebugOptions) => void;
  onRegenerateWallSketch?: () => void;
  onRunPass?: (passName: string) => void;
  /** Capture the full skeleton (geometry + rooms + pipeline stack) as a fixture (downloads JSON). */
  onExportFixture?: () => void;
  /** Load a skeleton fixture JSON onto the current floor (for visual inspection). */
  onLoadFixture?: (json: string) => void;
}

export class SkeletonDebugWidget extends DebugControlsWidget {
  static readonly WIDGET_NAME = 'skeleton-debug';

  private viewer: IViewerScene;
  private pipeline: SkeletonProcessingPipeline | null = null;
  private callbacks: SkeletonDebugCallbacks;
  private pipelineControlPanel: PipelineControlPanel | null = null;

  private debugOptions: SkeletonDebugOptions = {
    showRaycasts: false,
    showBisectors: false,
    showStraightenRays: false,
    showSourceVertices: false,
    showProbeOrigins: false,
    showProbeRays: false,
    showProbeMidpoints: false,
    showProbeCenterlines: false,
    showPairWallsMatchNear: false,
    showPairWallsRayNear: false,
    showPairWallsMatchInf: false,
    showPairWallsRayInf: false,
    showPruneMatches: false,
    showCornerMatches: false,
    showSplitLines: false,
    showRasterImage: false,
    showAllTriangles: false,
  };

  constructor(viewer: IViewerScene, callbacks: SkeletonDebugCallbacks = {}) {
    super(SkeletonDebugWidget.WIDGET_NAME);
    this.viewer = viewer;
    this.callbacks = callbacks;
    this.setupControls();
  }

  private setupControls(): void {
    // Add visualization toggles
    this.addControl('raycasts', {
      type: 'checkbox',
      label: 'Show Raycasts',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showRaycasts = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_RAYCAST_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('bisectors', {
      type: 'checkbox',
      label: 'Show Bisector Directions',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showBisectors = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_BISECTOR_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('straightenRays', {
      type: 'checkbox',
      label: 'Show Straighten Rays',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showStraightenRays = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_STRAIGHTEN_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('sourceVertices', {
      type: 'checkbox',
      label: 'Show Source Vertices',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showSourceVertices = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_SOURCE_VERTEX_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('probeOrigins', {
      type: 'checkbox',
      label: 'Show Probe Origins',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showProbeOrigins = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PROBE_ORIGIN_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('probeRays', {
      type: 'checkbox',
      label: 'Show Probe Rays',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showProbeRays = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PROBE_RAY_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('probeMidpoints', {
      type: 'checkbox',
      label: 'Show Probe Midpoints',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showProbeMidpoints = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PROBE_MIDPOINT_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('probeCenterlines', {
      type: 'checkbox',
      label: 'Show Probe Centerlines',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showProbeCenterlines = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PROBE_CENTERLINE_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    // Pair-walls per-stage toggles. Each stage runs in escalation
    // order; toggling them independently shows exactly which stage
    // contributed which ray.
    this.addControl('pairWallsMatchNear', {
      type: 'checkbox',
      label: 'Pair Walls: Match ≤ 50 cm',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showPairWallsMatchNear = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PAIR_WALLS_MATCH_NEAR_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('pairWallsRayNear', {
      type: 'checkbox',
      label: 'Pair Walls: Ray ≤ 50 cm',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showPairWallsRayNear = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PAIR_WALLS_RAY_NEAR_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('pairWallsMatchInf', {
      type: 'checkbox',
      label: 'Pair Walls: Match ∞',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showPairWallsMatchInf = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PAIR_WALLS_MATCH_INF_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    this.addControl('pairWallsRayInf', {
      type: 'checkbox',
      label: 'Pair Walls: Ray ∞',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showPairWallsRayInf = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PAIR_WALLS_RAY_INF_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    // Prune-pass matching ties: which polygon edge locked which trace
    // (green) and which candidates were rejected (red).
    this.addControl('pruneMatches', {
      type: 'checkbox',
      label: 'Show Prune Matches',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showPruneMatches = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_PRUNE_MATCH_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    // Thickness corner-match ties: which polygon corner each skeleton node
    // claimed on each side (drives the measured wall thickness).
    this.addControl('cornerMatches', {
      type: 'checkbox',
      label: 'Show Corner Matches',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showCornerMatches = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_CORNER_MATCH_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    // Per-element wall partition boundaries (2D), for debugging the split.
    this.addControl('splitLines', {
      type: 'checkbox',
      label: 'Show Wall Split Lines',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showSplitLines = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_SPLIT_LINES_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    // Rasterized wall region (the input to the raster-thin skeleton pass).
    // Only populated after the 'raster_skeleton' pass has run.
    this.addControl('rasterImage', {
      type: 'checkbox',
      label: 'Show Rasterized Walls',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showRasterImage = checked;
        toggleDebugGroupVisibility(this.viewer, SKELETON_RASTER_DEBUG_GROUP_NAME, checked);
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    // Show every mesh triangle edge (no crease filter), for debugging the
    // raw triangulation produced by Manifold's union/cut.
    this.addControl('allTriangles', {
      type: 'checkbox',
      label: 'Show All Triangles',
      checked: false,
      onChange: (checked) => {
        this.debugOptions.showAllTriangles = checked;
        this.callbacks.onDebugOptionsChange?.(this.debugOptions);
      },
    });

    // Add separator
    this.addSeparator();

    // Add regenerate button
    this.addControl('regenerate', {
      type: 'button',
      label: '🔄 Regenerate WallSketch',
      onClick: () => {
        this.callbacks.onRegenerateWallSketch?.();
      },
    });

    // Export EVERYTHING about the skeleton in one fixture: nodes/edges (+thickness
    // +roomIds), wallRegions, floorGroups, extruded geometry, room/outline
    // polygons, AND the pipeline stack. Self-contained for bug reports + tests.
    this.addControl('exportFixture', {
      type: 'button',
      label: '🧪 Export WallSketch (full)',
      onClick: () => {
        this.callbacks.onExportFixture?.();
      },
    });

    // Load a skeleton fixture JSON onto the current floor (visual inspection of
    // captured/test fixtures). Opens a file picker, reads the file as text, and
    // hands the JSON to the tool. In-memory only — does not persist the edit.
    this.addControl('loadFixture', {
      type: 'button',
      label: '📂 Load WallSketch Fixture',
      onClick: () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/json,.json';
        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => this.callbacks.onLoadFixture?.(String(reader.result ?? ''));
          reader.onerror = () => console.warn('Load fixture: failed to read file', reader.error);
          reader.readAsText(file);
        };
        input.click();
      },
    });

    // Add separator before pipeline section
    this.addSeparator();

    // Create PipelineControlPanel for managing pipeline passes
    this.pipelineControlPanel = new PipelineControlPanel({
      onPipelineChanged: () => {
        // Trigger re-run when pipeline configuration changes
        this.callbacks.onRunPass?.('__all__');
      },
    });

    // Add the pipeline control panel element to the container
    this.container.appendChild(this.pipelineControlPanel.getElement());
  }

  /**
   * Add a separator line to the widget
   */
  private addSeparator(): void {
    const separator = document.createElement('div');
    separator.style.cssText = `
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
      margin: 12px 0;
    `;
    this.container.appendChild(separator);
  }

  /**
   * Set the processing pipeline and add it to the control panel
   */
  setPipeline(pipeline: SkeletonProcessingPipeline): void {
    this.pipeline = pipeline;

    if (this.pipelineControlPanel) {
      // Add the skeleton pipeline as a tab
      this.pipelineControlPanel.addTab({
        id: 'skeleton',
        label: 'WallSketch Pipeline',
        pipeline: pipeline as unknown as PipelineInterface,
      });
    }
  }

  /**
   * Refresh the pipeline control panel UI
   * Called after pipeline execution to update timings display
   */
  updateTimings(): void {
    if (this.pipelineControlPanel) {
      this.pipelineControlPanel.refreshPassesList();
    }
  }

  /**
   * Set the debug options change callback
   */
  setOnDebugOptionsChange(callback: (options: SkeletonDebugOptions) => void): void {
    this.callbacks.onDebugOptionsChange = callback;
  }

  /**
   * Set the regenerate callback
   */
  setOnRegenerateWallSketch(callback: () => void): void {
    this.callbacks.onRegenerateWallSketch = callback;
  }

  /**
   * Set the run pass callback
   */
  setOnRunPass(callback: (passName: string) => void): void {
    this.callbacks.onRunPass = callback;
  }

  /**
   * Get current debug options
   */
  getDebugOptions(): SkeletonDebugOptions {
    return { ...this.debugOptions };
  }
}
